import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { parishService, SubParishDto, MyParishesResponse } from '../../services/parishService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const ACCESS_LEVELS = [
  { value: 'leitura', label: 'Leitura — só visualiza os eventos' },
  { value: 'eventos', label: 'Eventos — cria e edita eventos' },
  { value: 'operacao', label: 'Operação — eventos, inscrições e check-in' },
] as const;

type AccessLevel = (typeof ACCESS_LEVELS)[number]['value'];

function generateStrongPassword(length = 16): string {
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  const symbols = '!@#$%&*-_?';
  const all = lower + upper + digits + symbols;
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  const chars = [
    lower[bytes[0] % lower.length],
    upper[bytes[1] % upper.length],
    digits[bytes[2] % digits.length],
    symbols[bytes[3] % symbols.length],
  ];
  for (let i = 4; i < length; i += 1) {
    chars.push(all[bytes[i] % all.length]);
  }
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = bytes[i % bytes.length] % (i + 1);
    const swap = chars[i];
    chars[i] = chars[j];
    chars[j] = swap;
  }
  return chars.join('');
}

function levelLabel(level?: string) {
  return ACCESS_LEVELS.find((item) => item.value === level)?.label.split(' — ')[0] || 'Eventos';
}

const SubParishesPage: React.FC = () => {

  const [data, setData] = useState<MyParishesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    password: '',
    description: '',
    sectorAccessLevel: 'eventos' as AccessLevel,
  });
  const [showPassword, setShowPassword] = useState(false);

  const [openEdit, setOpenEdit] = useState(false);
  const [editingParish, setEditingParish] = useState<SubParishDto | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    sectorAccessLevel: 'eventos' as AccessLevel,
    password: '',
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const [openDelete, setOpenDelete] = useState(false);
  const [deletingParish, setDeletingParish] = useState<SubParishDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [copied, setCopied] = useState(false);

  const accessUrl = data?.sectorAccess?.path
    ? `${window.location.origin}${data.sectorAccess.path}`
    : '';
  const maxSectors = data?.sectorAccess?.max;
  const usedSectors = data?.sectorAccess?.used ?? data?.subParishes?.length ?? 0;
  const atLimit = typeof maxSectors === 'number' && usedSectors >= maxSectors;

  const load = () => {
    setLoading(true);
    setError(null);
    parishService
      .getMyParishes()
      .then(setData)
      .catch((e) => setError(e.message || 'Erro ao carregar paróquias'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setError('Preencha o nome do setor.');
      return;
    }
    if (!form.description.trim()) {
      setError('Preencha a descrição.');
      return;
    }
    if (!form.password || form.password.length < 6) {
      setError('Senha é obrigatória (mínimo 6 caracteres).');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await parishService.createSubParish({
        name: form.name.trim(),
        password: form.password,
        description: form.description.trim(),
        sectorAccessLevel: form.sectorAccessLevel,
      });
      setOpenCreate(false);
      setForm({
        name: '',
        password: '',
        description: '',
        sectorAccessLevel: 'eventos',
      });
      load();
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || 'Erro ao criar setor';
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (p: SubParishDto) => {
    setEditingParish(p);
    setEditForm({
      name: p.name ?? '',
      description: p.description ?? '',
      sectorAccessLevel: (p.sectorAccessLevel || 'eventos') as AccessLevel,
      password: '',
    });
    setOpenEdit(true);
  };

  const handleEdit = async () => {
    if (!editingParish) return;
    if (!editForm.name.trim() || !editForm.description.trim()) {
      setError('Nome e descrição são obrigatórios.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await parishService.updateSubParish(editingParish._id, {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        sectorAccessLevel: editForm.sectorAccessLevel,
        password: editForm.password.trim().length >= 6 ? editForm.password : undefined,
      });
      setOpenEdit(false);
      setEditingParish(null);
      load();
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || 'Erro ao atualizar setor';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (p: SubParishDto) => {
    setDeletingParish(p);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    if (!deletingParish) return;
    setDeleting(true);
    setError(null);
    try {
      await parishService.deleteSubParish(deletingParish._id);
      setOpenDelete(false);
      setDeletingParish(null);
      load();
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || 'Erro ao excluir setor';
      setError(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Setores
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)}
          disabled={atLimit}
        >
          Novo acesso
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Cada acesso é um setor. Ele entra pelo link abaixo, com nome e senha,
            e cria eventos conforme o nível que você definir. Você continua vendo os eventos de todos os setores.
            {typeof maxSectors === 'number'
              ? ` Limite: ${usedSectors} de ${maxSectors}.`
              : ` Em uso: ${usedSectors}.`}
          </Typography>
          {accessUrl && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography variant="caption" color="text.secondary">Link de acesso dos setores</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{accessUrl}</Typography>
              </Box>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => {
                  void navigator.clipboard.writeText(accessUrl).then(() => {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                  });
                }}
              >
                {copied ? 'Copiado' : 'Copiar link'}
              </Button>
            </Paper>
          )}
          {atLimit && (
            <Alert severity="info" sx={{ mb: 2 }}>
              O limite de setores foi atingido.
            </Alert>
          )}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Permissão</TableCell>
                  <TableCell align="right" width={100}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.subParishes ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhum setor cadastrado. Clique em &quot;Novo acesso&quot; para criar.
                    </TableCell>
                  </TableRow>
                ) : (
                  (data?.subParishes ?? []).map((p: SubParishDto) => (
                    <TableRow key={p._id}>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.description || '—'}</TableCell>
                      <TableCell>{levelLabel(p.sectorAccessLevel)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEditDialog(p)}
                          aria-label="Editar setor"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(p)}
                          aria-label="Excluir setor"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={openCreate} onClose={() => !creating && setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo acesso de setor</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Nome"
            fullWidth
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ex: Liturgia"
            helperText="Esse nome é o usuário no link de acesso"
          />
          <TextField
            margin="dense"
            label="Senha"
            fullWidth
            required
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Mínimo 6 caracteres"
            helperText="Use Gerar para criar uma senha forte de 16 caracteres."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={() => {
                      setForm((f) => ({ ...f, password: generateStrongPassword() }));
                      setShowPassword(true);
                    }}
                    sx={{ mr: 0.5, minWidth: 0, textTransform: 'none' }}
                  >
                    Gerar
                  </Button>
                  <IconButton
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            required
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Ex: Equipe que organiza os eventos de música"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="sector-level-label">Permissão</InputLabel>
            <Select
              labelId="sector-level-label"
              label="Permissão"
              value={form.sectorAccessLevel}
              onChange={(e) => setForm((f) => ({ ...f, sectorAccessLevel: e.target.value as AccessLevel }))}
            >
              {ACCESS_LEVELS.map((level) => (
                <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={24} /> : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => !saving && (setOpenEdit(false), setEditingParish(null))} maxWidth="sm" fullWidth>
        <DialogTitle>Editar acesso</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Nome"
            fullWidth
            required
            value={editForm.name}
            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            required
            multiline
            rows={2}
            value={editForm.description}
            onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="sector-level-edit-label">Permissão</InputLabel>
            <Select
              labelId="sector-level-edit-label"
              label="Permissão"
              value={editForm.sectorAccessLevel}
              onChange={(e) => setEditForm((f) => ({ ...f, sectorAccessLevel: e.target.value as AccessLevel }))}
            >
              {ACCESS_LEVELS.map((level) => (
                <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Nova senha (opcional)"
            fullWidth
            type={showEditPassword ? 'text' : 'password'}
            value={editForm.password}
            onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Deixe em branco para não alterar. Mín. 6 caracteres"
            helperText="Use Gerar para criar uma senha forte de 16 caracteres."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={() => {
                      setEditForm((f) => ({ ...f, password: generateStrongPassword() }));
                      setShowEditPassword(true);
                    }}
                    sx={{ mr: 0.5, minWidth: 0, textTransform: 'none' }}
                  >
                    Gerar
                  </Button>
                  <IconButton
                    aria-label={showEditPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    onClick={() => setShowEditPassword((s) => !s)}
                    edge="end"
                  >
                    {showEditPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => !deleting && (setOpenDelete(false), setDeletingParish(null))}>
        <DialogTitle>Excluir setor</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja excluir o setor <strong>{deletingParish?.name}</strong>? Ele será desativado e não poderá mais acessar o sistema.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} disabled={deleting}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SubParishesPage;
